import { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
} from "@mui/material";

import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import { useSnackbar } from "notistack";

import PageHeader from "../../components/common/PageHeader";
import FilterBar from "../../components/common/FilterBar";
import EmptyState from "../../components/common/EmptyState";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import FormModal from "../../components/common/FormModal";
import ViewModal from "../../components/common/ViewModal";

import {
  useListProvidersQuery,
  useCreateProviderMutation,
  useUpdateProviderMutation,
} from "../../features/masterdata/masterdataApi";

import type { Provider, ProviderCreate, ProviderType } from "../../features/masterdata/types";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { usePersistedListFilters } from "../../hooks/usePersistedListFilters";

const PROVIDER_TYPES: { value: ProviderType; label: string }[] = [
  { value: "bus_company", label: "Bus company" },
  { value: "taxi", label: "Taxi" },
  { value: "private_driver", label: "Private driver" },
  { value: "other", label: "Other" },
];

function typeLabel(t: string) {
  return PROVIDER_TYPES.find((x) => x.value === t)?.label ?? t;
}

type ProviderOrdering = "name" | "-name" | "created_at" | "-created_at";

export default function ProvidersPage() {
  const { enqueueSnackbar } = useSnackbar();

  const [filters, setFilters] = usePersistedListFilters("providers", {
    type: "" as ProviderType | "",
    isActive: "" as "" | "true" | "false",
    search: "",
    ordering: "name" as ProviderOrdering,
  });
  const { type, isActive, search, ordering } = filters;
  const debouncedSearch = useDebouncedValue(search);

  const [page, setPage] = useState(1);

  const toggleSort = (field: "name" | "created_at") => {
    setFilters((prev) => ({
      ...prev,
      ordering: (prev.ordering === field ? `-${field}` : prev.ordering === `-${field}` ? field : field) as ProviderOrdering,
    }));
  };
  const sortIcon = (field: "name" | "created_at") => {
    if (ordering === field) return " ↑";
    if (ordering === `-${field}`) return " ↓";
    return " ⇅";
  };

  // dialogs
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState<Provider | null>(null);

  const [openView, setOpenView] = useState(false);
  const [viewing, setViewing] = useState<Provider | null>(null);

  const [openDelete, setOpenDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Provider | null>(null);

  const [form, setForm] = useState<ProviderCreate>({
    type: "bus_company",
    name: "",
    contact_name: null,
    email: null,
    phone: null,
    notes: null,
    is_active: true,
  });

  // reset page on filter changes
  useEffect(() => {
    setPage(1);
  }, [type, isActive, debouncedSearch, ordering]);

  const { data, isLoading, isFetching, error } = useListProvidersQuery({
    type: type || undefined,
    is_active: isActive === "" ? undefined : isActive === "true",
    search: debouncedSearch,
    ordering,
    page,
  });

  const rows = data?.results ?? [];
  const count = data?.count ?? 0;
  const canPrev = !!data?.previous && page > 1;
  const canNext = !!data?.next;

  const [createProvider, { isLoading: isCreating }] = useCreateProviderMutation();
  const [updateProvider, { isLoading: isUpdating }] = useUpdateProviderMutation();
  // Delete not supported by backend; confirm dialog shows info message

  const saving = isCreating || isUpdating;

  const showEmpty = !isLoading && rows.length === 0;

  // ------------------
  // Actions
  // ------------------
  const onOpenCreate = () => {
    setEditing(null);
    setForm({
      type: "bus_company",
      name: "",
      contact_name: null,
      email: null,
      phone: null,
      notes: null,
      is_active: true,
    });
    setOpenForm(true);
  };

  const onOpenEdit = (p: Provider) => {
    setEditing(p);
    setForm({
      type: p.type,
      name: p.name ?? "",
      contact_name: p.contact_name ?? null,
      email: p.email ?? null,
      phone: p.phone ?? null,
      notes: p.notes ?? null,
      is_active: !!p.is_active,
    });
    setOpenForm(true);
  };

  const onOpenView = (p: Provider) => {
    setViewing(p);
    setOpenView(true);
  };

  const onSubmitForm = async () => {
    try {
      if (!form.name.trim()) return;

      const payload: ProviderCreate = {
        ...form,
        name: form.name.trim(),
        contact_name: form.contact_name || null,
        email: form.email || null,
        phone: form.phone || null,
        notes: form.notes || null,
        is_active: !!form.is_active,
      };

      if (!editing) {
        await createProvider(payload).unwrap();
        enqueueSnackbar("Provider created", { variant: "success" });
      } else {
        await updateProvider({ id: editing.id, patch: payload }).unwrap();
        enqueueSnackbar("Provider updated", { variant: "success" });
      }

      setOpenForm(false);
      setEditing(null);
      setPage(1);
    } catch (e: any) {
      const msg = e?.data?.detail || "Failed to save provider";
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  const onConfirmDelete = async () => {
    if (!deleteTarget) return;
    setOpenDelete(false);
    setDeleteTarget(null);
    setPage(1);
    enqueueSnackbar("Deleting providers is not supported. Deactivate instead.", { variant: "info" });
  };

  return (
    <Box>
      <PageHeader
        title="Providers"
        subtitle="Bus companies, taxis, private drivers"
        actions={
          <Button variant="contained" startIcon={<AddOutlinedIcon />} onClick={onOpenCreate}>
            New Provider
          </Button>
        }
      />

      <FilterBar loading={isLoading || isFetching}>
        <TextField
          label="Type"
          select
          value={type}
          onChange={(e) => setFilters({ type: e.target.value as ProviderType | "" })}
          size="small"
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          {PROVIDER_TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Active"
          select
          value={isActive}
          onChange={(e) => setFilters({ isActive: e.target.value as "" | "true" | "false" })}
          size="small"
          sx={{ minWidth: 130 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="true">Active</MenuItem>
          <MenuItem value="false">Inactive</MenuItem>
        </TextField>
        <TextField
          label="Search (name, email, contact)"
          value={search}
          onChange={(e) => setFilters({ search: e.target.value })}
          size="small"
          placeholder="Search…"
          sx={{ minWidth: 200, flex: 1 }}
        />
      </FilterBar>

      {error ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          Failed to load providers.
        </Paper>
      ) : showEmpty ? (
        <EmptyState
          title="No providers yet"
          description="Create providers to assign transfers."
          actionLabel="Create provider"
          onAction={onOpenCreate}
        />
      ) : (
        <>
          <Paper variant="outlined" sx={{ overflow: "auto", width: "100%" }}>
            <Table sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Box
                      component="span"
                      onClick={() => toggleSort("name")}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        cursor: "pointer",
                        fontWeight: 600,
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      Name{sortIcon("name")}
                    </Box>
                  </TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 800 }}>{r.name}</TableCell>
                    <TableCell>
                      <Chip size="small" label={typeLabel(r.type)} />
                    </TableCell>
                    <TableCell>{r.contact_name || "-"}</TableCell>
                    <TableCell>{r.email || "-"}</TableCell>
                    <TableCell>{r.phone || "-"}</TableCell>
                    <TableCell>{r.is_active ? "Yes" : "No"}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => onOpenView(r)} aria-label="view">
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => onOpenEdit(r)} aria-label="edit">
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      {/* <IconButton size="small" onClick={() => onAskDelete(r)} aria-label="delete">
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      </IconButton> */}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          {/* paging */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              Total: {count} • Page: {page}
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={isFetching || !canPrev}
              >
                Prev
              </Button>
              <Button onClick={() => setPage((p) => p + 1)} disabled={isFetching || !canNext}>
                Next
              </Button>
            </Stack>
          </Stack>
        </>
      )}

      <ViewModal
        open={openView}
        onClose={() => setOpenView(false)}
        title="Provider details"
        subtitle={
          viewing
            ? `${viewing.name} · ${typeLabel(viewing.type)} · ${viewing.is_active ? "Active" : "Inactive"}`
            : undefined
        }
        rows={
          viewing
            ? [
                { label: "Contact", value: viewing.contact_name },
                { label: "Email", value: viewing.email },
                { label: "Phone", value: viewing.phone },
                { label: "Notes", value: viewing.notes },
              ]
            : undefined
        }
        onEdit={
          viewing
            ? () => {
                onOpenEdit(viewing);
                setOpenView(false);
              }
            : undefined
        }
      />

      <FormModal
        open={openForm}
        onClose={() => setOpenForm(false)}
        title={editing ? "Edit provider" : "New provider"}
        onSubmit={onSubmitForm}
        submitDisabled={!form.name.trim()}
        loading={saving}
        maxWidth="sm"
      >
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Type"
              select
              value={form.type}
              onChange={(e) => setForm((p) => ({ ...p, type: e.target.value as ProviderType }))}
              fullWidth
            >
              {PROVIDER_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Company or person name"
              required
              autoFocus
              fullWidth
            />
          </Stack>
          <TextField
            label="Contact name (optional)"
            value={form.contact_name ?? ""}
            onChange={(e) => setForm((p) => ({ ...p, contact_name: e.target.value || null }))}
            placeholder="Primary contact"
            fullWidth
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Email (optional)"
              type="email"
              value={form.email ?? ""}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value || null }))}
              placeholder="email@example.com"
              fullWidth
            />
            <TextField
              label="Phone (optional)"
              value={form.phone ?? ""}
              onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value || null }))}
              placeholder="+30 …"
              fullWidth
            />
          </Stack>
          <TextField
            label="Notes (optional)"
            value={form.notes ?? ""}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value || null }))}
            placeholder="Internal notes"
            multiline
            minRows={2}
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={!!form.is_active}
                onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
              />
            }
            label="Active"
          />
        </Stack>
      </FormModal>

      {/* Delete confirm */}
      <ConfirmDialog
        open={openDelete}
        title="Delete provider?"
        description={deleteTarget ? `This will delete "${deleteTarget.name}".` : undefined}
        confirmText="Delete"
        severity="destructive"
        loading={false}
        onClose={() => {
          setOpenDelete(false);
          setDeleteTarget(null);
        }}
        onConfirm={onConfirmDelete}
      />
    </Box>
  );
}
